import { Builder, BuilderComponent, builder } from '@builder.io/react';
import { graphql } from 'gatsby';
import { Suspense } from 'react';
import customComponentsRegistration, { richTextModels } from 'templates/componentGenerator';

import ModalContainer from 'molecules/Modal/Modal';

import Footer from 'components/Footer/Footer';
import Header from 'components/Header/Header';
import SupportHero from 'components/Hero/HeroSupport/HeroSupport';
import Main from 'components/Main';
import SEO from 'components/seo';

import type { Query } from 'graphqlTypes';
import type { FC } from 'react';
import type { IHead } from 'templates/BuilderPage';

builder.init(process.env.GATSBY_BUILDER_KEY || '');

const SupportTemplate = (query: { data: Query }) => {
  const {
    data: {
      allBuilderModels: {
        oneSupport,
        oneGlobalBanner,
        oneComponentHeader: header,
        oneComponentFooter: footer,
        supportCategory,
        hiddenFormFields,
      },
    },
  } = query;

  const post = oneSupport?.content;
  const { banner } = { ...oneSupport?.data };
  const globalBanner = oneGlobalBanner?.data;

  const isCustomBanner = banner?.customBanner === true;
  const isGlobalBanner = globalBanner?.showBanner === true;
  const isHideBanner = banner?.hideBanner === true;

  customComponentsRegistration();

  return (
    <Suspense fallback>
      {header?.content && (
        <Header
          {...header.content.data}
          banner={(isCustomBanner && !isHideBanner && banner) || (isGlobalBanner && !isHideBanner && globalBanner)}
        />
      )}
      <Main>
        <SupportHero blogPost={post} />
        <BuilderComponent
          model="post"
          data={{
            template: 'support',
            entryCategories: JSON.stringify({ supportCategory }),
            hiddenFields: JSON.stringify({ hiddenFormFields }),
          }}
          content={post}
          options={{ includeRefs: true, cachebust: true }}
        />
      </Main>
      {footer?.content && <Footer isDark={true} content={footer.content.data} />}
      <ModalContainer />
    </Suspense>
  );
};

export default SupportTemplate;

export const Head: FC<IHead> = ({
  data: {
    allBuilderModels: { oneSupport },
  },
  location,
}) => (
  <>
    {oneSupport && (
      <SEO
        title={oneSupport?.data?.metaTitle || oneSupport?.data?.title}
        description={oneSupport?.data?.metaDescription || oneSupport?.data?.description}
        image={oneSupport?.data?.featuredImage}
        location={location}
        indexable={!oneSupport?.data?.noIndex}
      />
    )}
  </>
);

export const supportPostQuery = graphql`
  query getSupportPosts($path: String!) {
    allBuilderModels {
      oneSupport(target: { urlPath: $path }, options: { includeRefs: true, cachebust: true }) {
        content
        data {
          title
          category
          description
          url
          noIndex
          banner
          metaTitle
          metaDescription
          isDarkFooter
        }
      }
      oneGlobalBanner(
        options: { includeRefs: true, cachebust: true }
        target: { id: "b241c59d043b4cc7b0f608356bea42ef" }
      ) {
        data {
          showBanner
          body
          button
          heading
          includeIcon
        }
      }
      oneComponentHeader(
        options: { includeRefs: true, cachebust: true }
        target: { id: "8fe1129198a9478c9cc7346a40a63805" }
      ) {
        content
      }
      oneComponentFooter(
        options: { includeRefs: true, cachebust: true }
        target: { id: "a7c491a16336403c94432e7b3ebb419b" }
      ) {
        content
      }
      supportCategory(options: { includeRefs: true, cachebust: true }) {
        content
      }
      hiddenFormFields {
        data {
          hiddenFormField
        }
      }
    }
  }
`;

Builder.register('insertMenu', {
  name: 'Support Components',
  advanced: true,
  items: richTextModels.map(model => ({ name: model.config.name })),
});
